import type { Project } from '@bigint-project/core';

import { buildWbsExportRows } from './wbs-export';

/** UTF-8 BOM lets Excel reliably recognise Chinese CSV column values. */
export const UTF8_BOM = '\uFEFF';

const HEADER = [
  'WBS',
  'Task Name',
  'Start',
  'Finish',
  'Duration (hours)',
  'Progress (%)',
  'Work (hours)',
  'Cost',
  'Resources',
  'Predecessors',
] as const;

function csvField(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function csvRow(values: readonly (string | number)[]): string {
  return values.map(csvField).join(',');
}

/**
 * Converts a project to a deterministic RFC 4180 CSV document.
 *
 * The result is deliberately pure: UI layers may pass it to a native save
 * dialog, web download, clipboard, or test without importing browser/Tauri APIs.
 */
export function exportProjectCsv(project: Project): string {
  const rows = buildWbsExportRows(project).map((row) =>
    csvRow([
      row.wbs,
      row.name,
      row.start,
      row.finish,
      row.durationHours,
      row.percentComplete,
      row.workHours,
      row.cost,
      row.resources,
      row.predecessors,
    ]),
  );

  return `${UTF8_BOM}${[csvRow(HEADER), ...rows].join('\r\n')}\r\n`;
}

/** A conservative default for a future save dialog; no file I/O is performed here. */
export function suggestedProjectCsvFilename(project: Pick<Project, 'name'>): string {
  const baseName = project.name.trim().replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_') || 'project';
  return `${baseName}.csv`;
}
